import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import {
  CreateMssRequestDto,
  MssEmailDto,
  MssEmailStatus,
  MssProviderEmailDto,
  MssRequestDto,
  MssRequestStatus,
  UpdateMssRequestDto,
} from '@services/dtos'
import { PaginatedQuery } from '@services/api'
import { PaginatedDto } from '@services/extendedType'
import { MssService } from '@services/mssService'

export interface MssFilters extends PaginatedQuery {
  doctorId?: number
  status?: MssEmailStatus
}

export interface MssRequestFilters extends PaginatedQuery {
  status?: MssRequestStatus
}

export interface MssState {
  emails: PaginatedDto<MssProviderEmailDto>
  requests: PaginatedDto<MssRequestDto>
  filters: MssFilters
  requestFilters: MssRequestFilters
  loading: boolean
  requestLoading: boolean
  addressesByRpps: string[]
  loadingAddressesByRpps: boolean
}

const initialState: MssState = {
  addressesByRpps: [],
  loadingAddressesByRpps: false,
  emails: {
    datas: [],
    totalCount: 0,
  },
  requests: {
    datas: [],
    totalCount: 0,
  },
  requestFilters: {
    limit: 20,
    page: 0,
  },
  filters: {
    limit: 20,
    page: 0,
    status: MssEmailStatus.TO_PROCESS,
  },
  loading: false,
  requestLoading: false,
}

export const getEmails = createAsyncThunk<
  PaginatedDto<MssProviderEmailDto>,
  void
>('mss/emails/find', async (_, { getState }) => {
  const { filters } = (getState() as { mss: MssState }).mss
  return MssService.getEmails(filters)
})

export const getAddressesByRpps = createAsyncThunk<string[], string>(
  'mss/addresses/find-rpps',
  async (rpps: string) => MssService.getAddressesByRpps(rpps),
)

export const getRequests = createAsyncThunk<PaginatedDto<MssRequestDto>, void>(
  'mss/requests/find',
  async (_, { getState }) => {
    const { requestFilters } = (getState() as { mss: MssState }).mss
    return MssService.getRequests(requestFilters)
  },
)

export const declineEmail = createAsyncThunk<void, string>(
  'mss/emails/declined',
  async (messageId, { getState }) => {
    const { filters } = (getState() as { mss: MssState }).mss
    if (!filters.doctorId) return
    return MssService.declineEmail(filters.doctorId, messageId)
  },
)

export const createRequest = createAsyncThunk<
  MssRequestDto,
  { doctorId: number; messageId: string; dto: CreateMssRequestDto }
>('mss/request/create', async ({ doctorId, messageId, dto }) =>
  MssService.createRequest(doctorId, messageId, dto),
)

export const updateRequest = createAsyncThunk<
  MssRequestDto,
  { id: number; dto: UpdateMssRequestDto }
>('mss/request/update', async ({ id, dto }) =>
  MssService.updateRequest(id, dto),
)

export const mssReducer = createSlice({
  name: 'mss',
  initialState,
  reducers: {
    setMssFilters: (state, action: PayloadAction<MssFilters>) => {
      state.filters = {
        ...state.filters,
        page: 0,
        ...action.payload,
      }
    },
    setMssRequestFilters: (state, action: PayloadAction<MssRequestFilters>) => {
      state.requestFilters = {
        ...state.requestFilters,
        page: 0,
        ...action.payload,
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getEmails.pending, (state) => {
        state.loading = true
      })
      .addCase(getEmails.rejected, (state) => {
        state.loading = false
      })
      .addCase(getEmails.fulfilled, (state, action) => {
        state.emails = action.payload
        state.loading = false
      })
      .addCase(getAddressesByRpps.pending, (state) => {
        state.loadingAddressesByRpps = true
      })
      .addCase(getAddressesByRpps.rejected, (state) => {
        state.loadingAddressesByRpps = false
      })
      .addCase(getAddressesByRpps.fulfilled, (state, action) => {
        state.addressesByRpps = action.payload
        state.loadingAddressesByRpps = false
      })
      .addCase(getRequests.pending, (state) => {
        state.requestLoading = true
      })
      .addCase(getRequests.rejected, (state) => {
        state.requestLoading = false
      })
      .addCase(getRequests.fulfilled, (state, action) => {
        state.requests = action.payload
        state.requestLoading = false
      })
  },
})

export default mssReducer.reducer

export const { setMssFilters, setMssRequestFilters } = mssReducer.actions
